import React from "react";
import { useTranslation } from "react-i18next";
import { ListTree, Plus, ChevronRight, Edit2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import InputText from "@/components/common/InputText";
import type { Task } from "@/api/mutation/useTaskCreateMutation";
import RedFlag from "@/assets/svg/RedFlag.svg";
import OrangeFlag from "@/assets/svg/OrangeFlag.svg";
import GreenFlag from "@/assets/svg/GreenFlag.svg";

interface TaskSubtasksListProps {
  subtasks?: Task[];
  allTasks?: Task[];
  currentTaskId?: string;
  onAddSubtask: (name: string) => void;
  onAttachExistingTask?: (task: Task) => void;
  onSelectSubtask: (subtaskId: string) => void;
  onDeleteSubtask?: (subtaskId: string) => void;
  onEditSubtaskName?: (subtaskId: string, newName: string) => void;
  allowed: boolean;
}

export const formatSubtaskDate = (dateVal?: Date | string | null): string => {
  if (!dateVal) return "-";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const TaskSubtasksList: React.FC<TaskSubtasksListProps> = ({
  subtasks = [],
  allTasks = [],
  currentTaskId,
  onAddSubtask,
  onAttachExistingTask,
  onSelectSubtask,
  onEditSubtaskName,
  allowed,
}) => {
  const { t } = useTranslation();
  const [showAddRow, setShowAddRow] = React.useState(false);
  const [newSubtaskName, setNewSubtaskName] = React.useState("");
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = React.useState<string | null>(null);
  const [editingName, setEditingName] = React.useState("");

  const suggestedTasks = React.useMemo(() => {
    if (!newSubtaskName.trim() || !allTasks.length) return [];
    const query = newSubtaskName.toLowerCase().trim();
    return allTasks.filter((t) => {
      if (t.taskId === currentTaskId) return false;
      if (t.parentTaskId === currentTaskId) return false;
      if (subtasks.some((existing) => existing.taskId === t.taskId)) return false;
      return t.taskName.toLowerCase().includes(query);
    });
  }, [newSubtaskName, allTasks, currentTaskId, subtasks]);

  const handleAddSubmit = () => {
    if (!newSubtaskName.trim()) return;
    onAddSubtask(newSubtaskName.trim());
    setNewSubtaskName("");
    setShowSuggestions(false);
    setShowAddRow(false);
  };

  const handleSelectSuggestedTask = (task: Task) => {
    if (onAttachExistingTask) {
      onAttachExistingTask(task);
    } else {
      onAddSubtask(task.taskName);
    }
    setNewSubtaskName("");
    setShowSuggestions(false);
    setShowAddRow(false);
  };

  const handleEditSubmit = (subtaskId: string) => {
    if (!editingName.trim()) return;
    if (onEditSubtaskName) {
      onEditSubtaskName(subtaskId, editingName.trim());
    }
    setEditingSubtaskId(null);
    setEditingName("");
  };

  const renderFlagIcon = (flag?: string) => {
    const normalized = flag?.trim().toLowerCase();
    if (normalized === "orange") {
      return <img src={OrangeFlag} alt={t("MODERATE_DELAY")} className="w-4 h-4 shrink-0 object-contain" />;
    }
    if (normalized === "red") {
      return <img src={RedFlag} alt={t("SIGNIFICANT_DELAY")} className="w-4 h-4 shrink-0 object-contain" />;
    }
    return <img src={GreenFlag} alt={t("ON_TIME")} className="w-4 h-4 shrink-0 object-contain" />;
  };

  const renderStatusBadge = (flag?: string) => {
    const normalized = flag?.trim().toLowerCase();
    if (normalized === "orange") {
      return (
        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-600 border border-amber-100 whitespace-nowrap">
          {t("MODERATE_DELAY")}
        </span>
      );
    }
    if (normalized === "red") {
      return (
        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-600 border border-rose-100 whitespace-nowrap">
          {t("SIGNIFICANT_DELAY")}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100 whitespace-nowrap">
        {t("ON_TIME")}
      </span>
    );
  };

  const getProgressColorClass = (flag?: string) => {
    const normalized = flag?.trim().toLowerCase();
    if (normalized === "orange") return "bg-amber-500";
    if (normalized === "red") return "bg-rose-500";
    return "bg-emerald-500";
  };

  return (
    <div className="flex flex-col gap-3 p-4 bg-white border border-slate-200/90 rounded-2xl shadow-2xs">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <ListTree className="w-4 h-4 text-slate-500" />
          <span>{t("sub_tasks")}</span>
          <span className="text-xs font-semibold text-slate-400">
            ({subtasks.length})
          </span>
        </div>

        {!showAddRow && allowed && (
          <Button
            type="button"
            variant="none"
            size="sm"
            onClick={() => setShowAddRow(true)}
            className="flex items-center gap-1.5 h-8 px-3 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-2xs transition-all cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5 text-white" />
            <span>{t("add")}</span>
          </Button>
        )}
      </div>

      {/* Add Inline Row */}
      {showAddRow && (
        <div className="relative flex flex-col gap-1 p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <InputText
                autoFocus
                value={newSubtaskName}
                onChange={(e) => {
                  setNewSubtaskName(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddSubmit();
                  if (e.key === "Escape") {
                    setShowAddRow(false);
                    setShowSuggestions(false);
                    setNewSubtaskName("");
                  }
                }}
                placeholder={t("enter_subtask_name") || "Enter subtask name..."}
                className="h-8 text-xs w-full bg-white"
              />

              {/* Suggestions Dropdown for Existing Task Attaching */}
              {showSuggestions && suggestedTasks.length > 0 && (
                <ul className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto py-1">
                  <div className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                    {t("attach_existing_task")}
                  </div>
                  {suggestedTasks.map((st) => (
                    <li
                      key={st.taskId}
                      className="px-3 py-2 text-xs font-medium text-slate-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer flex items-center justify-between border-b border-slate-50 last:border-none transition-colors"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectSuggestedTask(st);
                      }}
                    >
                      <span className="truncate flex-1 pr-2">{st.taskName}</span>
                      <span className="text-[10px] text-slate-400 shrink-0 bg-slate-100 px-1.5 py-0.5 rounded">
                        {t("attach")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleAddSubmit}
              className="h-8 px-3 text-xs font-semibold rounded-lg shadow-2xs"
            >
              {t("add")}
            </Button>
            <Button
              type="button"
              variant="none"
              size="sm"
              onClick={() => {
                setShowAddRow(false);
                setShowSuggestions(false);
                setNewSubtaskName("");
              }}
              className="h-8 px-2.5 text-xs text-slate-500 hover:bg-slate-200/60 rounded-lg"
            >
              {t("cancel")}
            </Button>
          </div>
        </div>
      )}

      {/* Subtasks Table / List */}
      <div className="flex flex-col overflow-x-auto">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-3 px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wider min-w-[620px]">
          <div className="col-span-4 sm:col-span-4">{t("sub_task")}</div>
          <div className="col-span-2 text-center">{t("status")}</div>
          <div className="col-span-2">{t("start")}</div>
          <div className="col-span-2">{t("end")}</div>
          <div className="col-span-2 text-right pr-4">{t("progress")}</div>
        </div>

        {subtasks.length === 0 && !showAddRow && (
          <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl text-xs text-slate-400 italic">
            {t("no_subtasks_added")}
          </div>
        )}

        <div className="flex flex-col gap-1.5 mt-1 max-h-80 overflow-y-auto pr-0.5 min-w-[620px]">
          {subtasks.map((rawSt) => {
            const matchedFromAll = allTasks.find((t) => t.taskId === rawSt.taskId);
            const st = {
              ...rawSt,
              ...matchedFromAll,
              taskName: rawSt.taskName || matchedFromAll?.taskName,
              flag: matchedFromAll?.flag || rawSt.flag,
              startDate: matchedFromAll?.startDate || rawSt.startDate,
              endDate: matchedFromAll?.endDate || rawSt.endDate,
              completionPecentage:
                matchedFromAll?.completionPecentage ?? rawSt.completionPecentage,
            };

            const progressVal = Math.min(
              Math.max(Number(st.completionPecentage ?? 0), 0),
              100
            );

            return (
              <div
                key={st.taskId}
                onClick={() => {
                  if (editingSubtaskId !== st.taskId) {
                    onSelectSubtask(st.taskId);
                  }
                }}
                className="group grid grid-cols-12 gap-3 items-center px-3 py-2.5 bg-slate-50/60 hover:bg-slate-100/90 border border-slate-200/60 rounded-xl text-xs transition-all cursor-pointer shadow-2xs select-none"
              >
                {/* Subtask Name & Flag */}
                <div
                  className="col-span-4 flex items-center gap-2.5 min-w-0 pr-1"
                  onClick={(e) => {
                    if (editingSubtaskId === st.taskId) {
                      e.stopPropagation();
                    }
                  }}
                >
                  <div className="shrink-0">{renderFlagIcon(st.flag)}</div>

                  {editingSubtaskId === st.taskId ? (
                    <div
                      className="flex items-center gap-1 flex-1 min-w-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <InputText
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleEditSubmit(st.taskId);
                          if (e.key === "Escape") setEditingSubtaskId(null);
                        }}
                        className="h-7 text-xs flex-1 bg-white"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSubmit(st.taskId);
                        }}
                        className="p-1 text-emerald-600 hover:text-emerald-700"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSubtaskId(null);
                        }}
                        className="p-1 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <span
                        className="font-medium text-slate-800 group-hover:text-primary-600 truncate transition-colors"
                        title={st.taskName}
                      >
                        {st.taskName}
                      </span>
                      {allowed && onEditSubtaskName && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSubtaskId(st.taskId);
                            setEditingName(st.taskName || "");
                          }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-slate-600 rounded transition-opacity"
                          title={t("edit")}
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Status Badge */}
                <div className="col-span-2 flex items-center justify-center">
                  {renderStatusBadge(st.flag)}
                </div>

                {/* Start Date */}
                <div className="col-span-2 text-slate-600 font-medium truncate">
                  {formatSubtaskDate(st.startDate)}
                </div>

                {/* End Date */}
                <div className="col-span-2 text-slate-600 font-medium truncate">
                  {formatSubtaskDate(st.endDate)}
                </div>

                {/* Progress Bar & Arrow */}
                <div className="col-span-2 flex items-center justify-end gap-2.5">
                  <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                    <span className="font-bold text-slate-800 text-[11px] shrink-0">
                      {progressVal}%
                    </span>
                    <div className="w-16 h-2 bg-slate-200/80 rounded-full overflow-hidden shrink-0">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${getProgressColorClass(
                          st.flag
                        )}`}
                        style={{ width: `${progressVal}%` }}
                      />
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TaskSubtasksList;
